import { useState, useEffect, useCallback } from "react";
import { ITEMS_PER_PAGE } from "../../../constants";
import {
  showErrorToast,
  getDuplicateSubmissionsReportsByType,
  updateTriggerOrMatchStatus
} from "../../../helpers";
import { useLoader } from "stm-frontend-library";
import { getSelectedJournalIds } from "../../../helpers/journals";

const useDuplicateSubmissions = ({ type, isActiveTab }) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [pageCount, setPageCount] = useState(0);

  const { showLoader, hideLoader } = useLoader();

  const [fetchParams, setFetchParams] = useState({
    currentPage: 0,
    searchTerm: '',
    type,
  });

  const fetchData = useCallback(async () => {
    try {
      showLoader();
      setError(false);

      const selectedJournalIds = await getSelectedJournalIds();

      const { currentPage, type, searchTerm } = fetchParams;
      const appliedSearchTerm = type === 'archived' ? searchTerm : '';
      const { data: { reports = [], total = 0 } = {} } =
        await getDuplicateSubmissionsReportsByType(
          currentPage * ITEMS_PER_PAGE + 1,
          ITEMS_PER_PAGE,
          type,
          appliedSearchTerm,
          selectedJournalIds
        );
      setData(reports);
      setPageCount(Math.ceil(total / ITEMS_PER_PAGE));
    } catch {
      showErrorToast();
      setError(true);
    } finally {
      hideLoader();
    }
  }, [fetchParams]);

  // Separate function to fetch all records for export
  const fetchAllDataForExport = useCallback(async () => {
    try {
      showLoader();
      setError(false);

      const selectedJournalIds = await getSelectedJournalIds();

      const { type, searchTerm } = fetchParams;
      const appliedSearchTerm = type === 'archived' ? searchTerm : '';
      
      // Fetch all records (up to 10,000) for export
      const { data: { reports = [] } = {} } =
        await getDuplicateSubmissionsReportsByType(
          1, // Start from first record
          10000, // Large number to get all records
          type,
          appliedSearchTerm,
          selectedJournalIds
        );
      
      return reports;
    } catch {
      showErrorToast();
      setError(true);
      return [];
    } finally {
      hideLoader();
    }
  }, [fetchParams]);

  useEffect(() => {
    if (isActiveTab) {
      fetchData();
    }
  }, [fetchData, isActiveTab]);

  const updateFetchParams = (newParams) => {
    setFetchParams(prev => ({
      ...prev,
      ...newParams,
      currentPage: newParams.currentPage !== undefined ? newParams.currentPage : prev.currentPage
    }));
  };

  const setTriggerOrMatchStatus = async (matchId, persistentId, status, additionalParams = {}) => {
    try {
      showLoader();
      await updateTriggerOrMatchStatus(matchId, persistentId, status, additionalParams);
    } catch (err) {
      showErrorToast('Error updating status');
      throw err;
    } finally {
      hideLoader();
    }
  };

  return {
    data,
    setData,
    error,
    pageCount,
    currentPage: fetchParams.currentPage,
    searchTerm: fetchParams.searchTerm,
    setSearchTerm: (term) => updateFetchParams({ searchTerm: term, currentPage: 0 }),
    setCurrentPage: (page) => updateFetchParams({ currentPage: page }),
    fetchData,
    fetchAllDataForExport,
    setTriggerOrMatchStatus
  };
};

export default useDuplicateSubmissions;
